import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Home, Hotel, Ticket, Check, Lock, ShoppingBag } from "lucide-react";
import { api, formatApiError, fmtDate, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang, usePick } from "@/i18n/LanguageContext";
import Modal from "@/components/Modal";
import { Label, Input, Textarea, PrimaryButton, GhostButton } from "@/components/fields";

const TIME_SLOTS = ["8:00 AM", "10:30 AM", "1:00 PM", "3:30 PM", "5:00 PM (sunset)"];

export default function ExcursionBookModal({ excursion, open, onClose }) {
  const { user } = useAuth();
  const { addItem } = useCart();
  const { t, lang } = useLang();
  const pick = usePick();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [villa, setVilla] = useState(null);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    guests: 2,
    date: "",
    time_slot: "",
    pickup_type: "villa",
    pickup_location: "",
    contact_name: "",
    contact_phone: "",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setConfirmation(null);
      setForm((f) => ({ ...f, contact_name: user?.name || f.contact_name }));
      api.get("/villa").then((r) => setVilla(r.data)).catch(() => {});
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !excursion || !form.date) {
      setQuote(null);
      return;
    }
    api.get(`/excursions/${excursion.id}/quote`, { params: { date: form.date, guests: Number(form.guests || 1) } })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [open, excursion, form.date]);

  const unitPrice = quote?.unit_price ?? excursion?.price ?? 0;
  const total = excursion ? unitPrice * Number(form.guests || 0) : 0;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const next = () => {
    if (step === 1) {
      if (!form.date) return toast.error(t("booking.errDate"));
      if (!form.time_slot) return toast.error(t("booking.errTime"));
    }
    if (step === 2 && form.pickup_type === "custom" && !form.pickup_location.trim()) {
      return toast.error(t("booking.errHotel"));
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!form.contact_name.trim() || !form.contact_phone.trim()) {
      return toast.error(t("booking.errContact"));
    }
    setSending(true);
    try {
      const { data } = await api.post("/bookings", {
        excursion_id: excursion.id,
        date: form.date,
        time_slot: form.time_slot,
        guests: Number(form.guests),
        pickup_type: form.pickup_type,
        pickup_location: form.pickup_type === "villa" ? "Villa Paraiso — main entrance" : form.pickup_location.trim(),
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        notes: form.notes,
        lang,
      });
      setConfirmation(data);
      setStep(4);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error(t("booking.signinToBook"));
        onClose();
        navigate("/auth", { state: { from: location.pathname } });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSending(false);
    }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/deposit", {
        booking_id: confirmation.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  const addToCart = () => {
    if (!form.date) return toast.error(t("booking.errDate"));
    if (!form.time_slot) return toast.error(t("booking.errTime"));
    addItem({
      kind: "excursion",
      excursion_id: excursion.id,
      label: pick(excursion, "title"),
      image: excursion.image,
      date: form.date,
      time_slot: form.time_slot,
      guests: Number(form.guests || 1),
      pickup_type: form.pickup_type,
      pickup_location: form.pickup_type === "villa" ? "Villa Paraiso — main entrance" : form.pickup_location.trim(),
      notes: form.notes,
      lang,
    });
    toast.success(t("cart.added"));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={step === 4 ? t("booking.bookedTitle") : t("booking.title")} testid="excursion-book-modal" wide>
      {!excursion ? null : step === 4 && confirmation ? (
        <div className="space-y-6" data-testid="booking-confirmation">
          <div className="rounded-3xl border-2 border-dashed border-terracotta/40 bg-white p-6 sm:p-8 space-y-5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-terracotta font-semibold text-sm">
                <Ticket size={18} /> {t("booking.pass")}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50" data-testid="booking-reference">
                {t("booking.ref")} {confirmation.id.slice(0, 8).toUpperCase()}
              </span>
            </div>
            <h4 className="font-serif text-3xl text-ink leading-tight">{confirmation.excursion_title}</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40 mb-1">{t("booking.dateTime")}</p>
                <p className="font-medium">{fmtDate(confirmation.date)} · {confirmation.time_slot}</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40 mb-1">{t("booking.guestsLbl")}</p>
                <p className="font-medium">{confirmation.guests} {confirmation.guests > 1 ? t("booking.people") : t("booking.person")}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40 mb-1">{t("booking.pickupLbl")}</p>
                <p className="font-medium">{confirmation.pickup_location}</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40 mb-1">{t("booking.total")}</p>
                <p className="font-semibold text-terracotta text-lg">${fmtUSD(confirmation.total_price)}</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40 mb-1">Status</p>
                <p className="font-medium">{t("booking.statusPending")}</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-ink/60 text-center">{t("booking.emailNote")}</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <PrimaryButton data-testid="pay-deposit-btn" onClick={payDeposit} disabled={paying}>
              <Lock size={15} /> {paying ? t("booking.openingCheckout") : t("booking.payDeposit", { amount: fmtUSD(confirmation.total_price) })}
            </PrimaryButton>
            <Link to="/account" data-testid="booking-view-trips-btn" onClick={onClose}>
              <GhostButton className="w-full sm:w-auto">{t("booking.viewTrips")}</GhostButton>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <img src={excursion.image} alt="" className="w-16 h-16 rounded-2xl object-cover" />
            <div>
              <h4 className="font-serif text-xl text-ink leading-tight">{pick(excursion, "title")}</h4>
              <p className="text-xs text-ink/50">
                ${excursion.price}{t("excursions.perPerson")} · {excursion.duration} · {excursion.difficulty}
              </p>
            </div>
            <div className="ml-auto flex gap-1.5">
              {[1, 2, 3].map((n) => (
                <span key={n} className={`h-1.5 w-8 rounded-full transition-colors ${step >= n ? "bg-terracotta" : "bg-ink/10"}`} />
              ))}
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-4" data-testid="booking-step-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bk-date">{t("booking.date")}</Label>
                  <Input id="bk-date" data-testid="booking-date-input" type="date" value={form.date} onChange={set("date")} />
                </div>
                <div>
                  <Label htmlFor="bk-guests">{t("booking.guests")}</Label>
                  <Input id="bk-guests" data-testid="booking-guests-input" type="number" min="1" max="20" value={form.guests} onChange={set("guests")} />
                </div>
              </div>
              <div>
                <Label>{t("booking.timeSlot")}</Label>
                <div className="flex flex-wrap gap-2">
                  {TIME_SLOTS.map((ts) => (
                    <button
                      key={ts}
                      type="button"
                      data-testid={`booking-time-${ts.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      onClick={() => setForm({ ...form, time_slot: ts })}
                      className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                        form.time_slot === ts ? "bg-terracotta text-sand border-terracotta" : "border-ink/15 hover:border-terracotta text-ink"
                      }`}
                    >
                      {ts}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="booking-step-2">
              <Label>{t("booking.pickup")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="booking-pickup-villa"
                  onClick={() => setForm({ ...form, pickup_type: "villa" })}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                    form.pickup_type === "villa" ? "border-terracotta bg-terracotta/5" : "border-ink/10 hover:border-terracotta/40"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Home size={20} className="text-terracotta" />
                    {form.pickup_type === "villa" && <Check size={16} className="text-terracotta" />}
                  </div>
                  <p className="font-semibold text-sm">{t("booking.villaOption")}</p>
                  <p className="text-xs text-ink/50 mt-1">{t("booking.villaOptionSub")}</p>
                </button>
                <button
                  type="button"
                  data-testid="booking-pickup-custom"
                  onClick={() => setForm({ ...form, pickup_type: "custom" })}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                    form.pickup_type === "custom" ? "border-terracotta bg-terracotta/5" : "border-ink/10 hover:border-terracotta/40"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Hotel size={20} className="text-teal" />
                    {form.pickup_type === "custom" && <Check size={16} className="text-terracotta" />}
                  </div>
                  <p className="font-semibold text-sm">{t("booking.customOption")}</p>
                  <p className="text-xs text-ink/50 mt-1">{t("booking.customOptionSub")}</p>
                </button>
              </div>
              {form.pickup_type === "custom" && (
                <div>
                  <Label htmlFor="bk-location">{t("booking.yourHotel")}</Label>
                  <Input
                    id="bk-location"
                    data-testid="booking-location-input"
                    value={form.pickup_location}
                    onChange={set("pickup_location")}
                    placeholder={t("booking.hotelPh")}
                  />
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="booking-step-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bk-name">{t("booking.contactName")}</Label>
                  <Input id="bk-name" data-testid="booking-name-input" value={form.contact_name} onChange={set("contact_name")} placeholder={t("booking.contactNamePh")} />
                </div>
                <div>
                  <Label htmlFor="bk-phone">{t("booking.phone")}</Label>
                  <Input id="bk-phone" data-testid="booking-phone-input" value={form.contact_phone} onChange={set("contact_phone")} placeholder="+1 ..." />
                </div>
              </div>
              <div>
                <Label htmlFor="bk-notes">{t("booking.notes")}</Label>
                <Textarea id="bk-notes" data-testid="booking-notes-input" value={form.notes} onChange={set("notes")} placeholder={t("booking.notesPh")} />
              </div>
              <div className="rounded-2xl bg-night text-sand p-5 flex items-center justify-between" data-testid="booking-summary">
                <div className="text-sm">
                  <p className="text-sand/60 text-xs font-mono uppercase tracking-[0.15em] mb-1">{t("booking.summary")}</p>
                  <p className="font-medium">
                    {pick(excursion, "title")} · {form.guests} × ${fmtUSD(unitPrice)}
                  </p>
                  {quote?.uplift_percent > 0 && (
                    <p className="text-gold text-xs mt-0.5" data-testid="booking-season-uplift">
                      {t("booking.seasonUplift", { season: quote.season_name, pct: quote.uplift_percent })}
                    </p>
                  )}
                  <p className="text-sand/60 text-xs mt-1">{fmtDate(form.date)} · {form.time_slot}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-3xl text-gold">${fmtUSD(total)}</p>
                  <p className="text-sand/60 text-xs mt-1">{t("booking.orDepositNow", { amount: fmtUSD(total) })}</p>
                </div>
              </div>
              <p className="text-xs text-ink/50">{t("booking.nothingCharged")}</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            {step > 1 ? (
              <GhostButton data-testid="booking-back-btn" onClick={() => setStep((s) => s - 1)}>
                <ArrowLeft size={16} /> {t("booking.back")}
              </GhostButton>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <GhostButton data-testid="booking-add-cart-btn" onClick={addToCart}>
                <ShoppingBag size={15} /> {t("cart.addBtn")}
              </GhostButton>
              {step < 3 ? (
                <PrimaryButton data-testid="booking-next-btn" onClick={next}>
                  {t("booking.continue")} <ArrowRight size={16} />
                </PrimaryButton>
              ) : (
                <PrimaryButton data-testid="booking-confirm-btn" onClick={submit} disabled={sending}>
                  {sending ? t("booking.confirming") : t("booking.confirm")} <Check size={16} />
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
